import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ApiError, get, post } from './api'
import type { Me } from './types'

// Who is signed in. Loaded once on start-up and refreshed after anything that changes it
// (sign-in, MFA, accepting an invite, creating a business).

interface AuthState {
  me: Me | null
  loading: boolean
  refresh: () => Promise<Me | null>
  signOut: (everywhere?: boolean) => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const next = await get<Me>('/v1/auth/me')
      setMe(next)
      return next
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setMe(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const signOut = useCallback(async (everywhere = false) => {
    try {
      await post('/v1/auth/logout', { everywhere })
    } finally {
      setMe(null)
    }
  }, [])

  const value = useMemo(() => ({ me, loading, refresh, signOut }), [me, loading, refresh, signOut])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth outside AuthProvider')
  return ctx
}

const INVITE_KEY = 'tabula:invite'

/** An invitation token from a link, kept until the person has signed in and it can be accepted. */
export const pendingInvite = {
  get: () => sessionStorage.getItem(INVITE_KEY),
  set: (token: string) => sessionStorage.setItem(INVITE_KEY, token),
  clear: () => sessionStorage.removeItem(INVITE_KEY),
}
